import { Fragment, useMemo, useState } from "react";
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@/components/ui/empty";
import { Spinner } from "@/components/ui/spinner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useMember } from "@/contexts/memberContext";
import { isMemberAnOrganisationOwner } from "@/core/auth";
import { tprStorage } from "@/core/storage";
import { useBookingsQuery } from "@/hooks/useBookingsQuery";
import { useOrganisationBookingsQuery } from "@/hooks/useOrganisationBookingsQuery";
import { hasTodayExpiredOccurrence, isRecurringActive } from "@/utils/bookingActiveness";
import { dedupeBookingsBySeries, getNextOccurrenceDate, humaniseBookingDate } from "@/lib/recurrence";
import { humaniseDateFriendly, humanizeDuration } from "@/lib/utils";
import { membershipTypeForDisplay, type MembershipType } from "@/types/MembershipType";
import { AlertCircle, ChevronDown, ChevronRight, Frown } from "lucide-react";
import { useNavigate } from "react-router";
import RecurrenceRenderer from "../RecurrenceRenderer/RecurrenceRenderer";
import LocationShorthandPill from "../LocationShorthandPill/LocationShorthandPill";
import OccurrenceTree from "./OccurrenceTree";
import type { Booking, BookingSort } from "@/types/Booking";
import type { Recurrence, Weekday } from "@/types/Recurrence";

const WEEKDAY_BY_INDEX: Weekday[] = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];

export type BookingsListMode = "active" | "active-expanded" | "cancelled";
const SHOW_MODE_KEY = "bookingsListShowMode";

/**
 * Slot bookings are inherently weekly recurrent, but legacy slot documents in the seed lack a
 * `recurrence` field. For the table caption ("Weekly, every Tuesday") we synthesise one from
 * the booking's own weekday so the row reads as a recurring entry. Returns null for genuinely
 * non-recurring bookings.
 */
function getDisplayRecurrence(booking: Booking): Recurrence | null {
  if (booking.recurrence) return booking.recurrence;
  if (booking.recurrencePattern) return booking.recurrencePattern;
  if (booking.type === "slot") {
    const date = booking.date instanceof Date ? booking.date : new Date(booking.date);
    return {
      baseBookingId: booking.id,
      frequency: "weekly",
      interval: 1,
      byWeekday: [WEEKDAY_BY_INDEX[date.getDay()]],
    } as Recurrence;
  }
  return null;
}

interface BookingWithDisplayDate extends Booking {
  displayDate: Date;
  displayDateLabel?: string;
}

/**
 * The first date a recurring series no longer occurs, or null if it isn't cancelled-from. A
 * recurring booking cancelled from a date keeps `isCancelled: false` (only `recurrence.cancelledFrom`
 * / `until` are set — see POST /booking/:id/cancel-from), deliberately, so the still-valid earlier
 * occurrences remain billable. We therefore detect "cancelled" via `cancelledFrom` here.
 */
function cancelledFromDate(booking: Booking): Date | null {
  const cf = booking.recurrence?.cancelledFrom;
  if (cf == null) return null;
  return cf instanceof Date ? cf : new Date(cf);
}

interface SimpleBookingsListProps {
  /**
   * When provided, the list filters down to bookings at this location only. The Dashboard
   * leaves this empty to show every booking the member can see; the location dashboard passes
   * its location id to scope the list.
   */
  locationId?: string;
  /**
   * Where to send the user when they click a row. Defaults to /dashboard. The location
   * dashboard overrides this with /location/<slug>.
   */
  returnUrl?: string;
}

const SimpleBookingsList = ({ locationId, returnUrl = "/dashboard" }: SimpleBookingsListProps = {}) => {
  const { member } = useMember();
  const isOwner = isMemberAnOrganisationOwner(member);
  const orgId = member?.ownerOfOrganisationId ?? member?.organisation?.id ?? "";
  // Org owners see their org's bookings on their own dashboard (dashboard only — not the per-location list).
  const includeOrg = isOwner && !!orgId && !locationId;
  const navigate = useNavigate();
  const [mode, setMode] = useState<BookingsListMode>(
    () => tprStorage.get<BookingsListMode>(SHOW_MODE_KEY) ?? "active",
  );
  const setShowMode = (next: BookingsListMode) => {
    setMode(next);
    tprStorage.set(SHOW_MODE_KEY, next);
  };
  const showActive = mode === "active" || mode === "active-expanded";

  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const toggleRow = (id: string) =>
    setExpandedRows((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const totalCols = locationId ? 5 : 6; // Next date, Time, [Location], Room, Type, Ref

  const sort: BookingSort = { field: "date", order: "desc" };

  // Server-side filter strategy: always filter by memberId (never location-wide). The server
  // ignores memberId when locationId is present and instead returns ALL bookings at the
  // location, sorted date desc and truncated to `limit` — on a busy centre that truncation can
  // paginate away a member's own near-term booking before the client ever sees it. Querying by
  // memberId keeps pagination scoped to the member (who never has hundreds of bookings), then we
  // scope to the location client-side below.
  const { data, isLoading, isError, error } = useBookingsQuery({
    showBaseOnly: true,
    filters: { memberId: member?.id, isCancelled: false },
    sort,
    limit: locationId ? 200 : 50,
    enabled: !!member?.id && (!locationId || !!locationId),
  });

  // Cancelled-mode query: only fired when the user toggles to "cancelled". The window of
  // interest is the last month → next year (R1) which we apply client-side after the fetch.
  const { data: cancelledData, isLoading: isLoadingCancelled } = useBookingsQuery({
    showBaseOnly: true,
    filters: { memberId: member?.id, isCancelled: true },
    sort,
    limit: locationId ? 200 : 100,
    enabled: !!member?.id && mode === "cancelled",
  });

  const { data: orgData } = useOrganisationBookingsQuery({
    organisationId: orgId,
    filters: { isCancelled: false },
    enabled: includeOrg,
  });
  const { data: orgCancelledData } = useOrganisationBookingsQuery({
    organisationId: orgId,
    filters: { isCancelled: true },
    enabled: includeOrg && mode === "cancelled",
  });

  const { upcoming: allBookings, expiredToday } = useMemo(() => {
    const ownArray = Array.isArray(data)
      ? data
      : data && "data" in data && Array.isArray(data.data)
        ? data.data
        : [];
    const orgArray = includeOrg ? (orgData?.data ?? []) : [];
    const byId = new Map<string, Booking>();
    for (const b of [...orgArray, ...ownArray]) byId.set(b.id, b); // own-query copy wins (scoped/trusted, better enriched)
    const bookingsArray = Array.from(byId.values());

    // When locationId is set the server returns EVERY booking at that location (it ignores the
    // memberId filter in that case — see the query comment above), so we must scope to both the
    // location AND the signed-in member client-side, or another member's bookings at the same
    // location would leak into this member's list. The dashboard case (no locationId) keeps the
    // full set, which for an org owner intentionally includes their org's bookings (includeOrg is
    // only ever true when locationId is absent). Cancelled bookings are filtered server-side via
    // the query but we also drop them here defensively so a stale React Query cache can never bleed
    // a cancelled row into the table.
    const scoped = (
      locationId
        ? bookingsArray.filter((b) => b.locationId === locationId && b.memberId === member?.id)
        : bookingsArray
    ).filter((b) => !b.isCancelled);

    // Deduplicate recurring bookings: prefer the base (has `recurrence`) over its instances
    // (which carry `recurrencePattern`). Shared with the dashboard "bookings today" count so the
    // two never diverge — see dedupeBookingsBySeries.
    const deduped: Booking[] = dedupeBookingsBySeries(scoped);

    const startOfTodayActive = new Date();
    startOfTodayActive.setHours(0, 0, 0, 0);

    const upcomingActive: Booking[] = [];
    const expired: Booking[] = [];
    for (const b of deduped) {
      // A recurring series cancelled from today or earlier has no future occurrences; it belongs in
      // the Cancelled tab, not Active. (Note the `type === "slot"` branch below would otherwise keep
      // a cancelled-from slot in Active forever, since slots ignore `until`.)
      const cf = cancelledFromDate(b);
      if (cf != null && cf <= startOfTodayActive) continue;

      if (b.type === "slot" || isRecurringActive(b)) {
        upcomingActive.push(b);
      }
      // A recurring booking can be both: today's slot has finished AND there's a future
      // occurrence ahead. We surface it in the expired list as well so users can confirm the
      // booking did happen today.
      if (hasTodayExpiredOccurrence(b)) {
        expired.push(b);
      }
    }

    const upcoming: BookingWithDisplayDate[] = upcomingActive
      .map((booking) => {
        const nextDate = getNextOccurrenceDate(booking);
        return {
          ...booking,
          displayDate: nextDate,
          // Caption swaps to "Now" when the booking is currently in progress.
          displayDateLabel: humaniseBookingDate(booking),
        };
      })
      .sort((a, b) => a.displayDate.getTime() - b.displayDate.getTime());

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiredToday: BookingWithDisplayDate[] = expired
      .map((booking) => ({
        ...booking,
        displayDate: today,
        displayDateLabel: "Today",
      }))
      .sort((a, b) => parseInt(a.startTime, 10) - parseInt(b.startTime, 10));

    return { upcoming, expiredToday };
  }, [data, orgData, includeOrg, locationId, member?.id]);

  const cancelledBookings = useMemo<BookingWithDisplayDate[]>(() => {
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);

    // (1) Hard-cancelled bookings (isCancelled === true) — from the cancelled-only query. This is how
    //     single-booking DELETE cancellations arrive.
    const ownArr = Array.isArray(cancelledData)
      ? cancelledData
      : cancelledData && "data" in cancelledData && Array.isArray(cancelledData.data)
        ? cancelledData.data
        : [];
    const orgArr = includeOrg ? (orgCancelledData?.data ?? []) : [];

    // (2) Recurring series cancelled FROM a past/today date. cancel-from leaves isCancelled:false, so
    //     these never appear in the cancelled-only query — they arrive via the active (isCancelled:false)
    //     query instead. Pull them from there so a staff/whole-series cancellation is still visible to
    //     the member (the reported bug).
    const ownActive = Array.isArray(data)
      ? data
      : data && "data" in data && Array.isArray(data.data)
        ? data.data
        : [];
    const orgActive = includeOrg ? (orgData?.data ?? []) : [];
    const fromDateCancelled = [...orgActive, ...ownActive].filter((b) => {
      const cf = cancelledFromDate(b);
      return cf != null && cf <= startOfToday;
    });

    const cancelledById = new Map<string, Booking>();
    for (const b of [...orgArr, ...ownArr]) cancelledById.set(b.id, b); // own-query copy wins (scoped/trusted, better enriched)
    for (const b of fromDateCancelled) if (!cancelledById.has(b.id)) cancelledById.set(b.id, b);
    const arr = Array.from(cancelledById.values());

    // Scope to both location AND signed-in member client-side when locationId is set, same as the
    // active-mode memo — the server returns the whole location (ignoring memberId) in that case.
    const scoped = (
      locationId ? arr.filter((b) => b.locationId === locationId && b.memberId === member?.id) : arr
    ).filter((b) => {
      if (b.isCancelled) return true;
      const cf = cancelledFromDate(b);
      return cf != null && cf <= startOfToday;
    });

    // Window: 1 month back → 1 year forward (R1). For a cancelled-from series the relevant date is
    // `cancelledFrom` (recent), not the — possibly long-past — genesis `date`.
    const now = new Date();
    const minDate = new Date(now);
    minDate.setMonth(minDate.getMonth() - 1);
    minDate.setHours(0, 0, 0, 0);
    const maxDate = new Date(now);
    maxDate.setFullYear(maxDate.getFullYear() + 1);
    maxDate.setHours(23, 59, 59, 999);

    return scoped
      .map((b) => {
        const cf = cancelledFromDate(b);
        const d = cf ?? (b.date instanceof Date ? b.date : new Date(b.date));
        return { ...b, displayDate: d, displayDateLabel: humaniseDateFriendly(d) };
      })
      .filter((b) => b.displayDate >= minDate && b.displayDate <= maxDate)
      .sort((a, b) => a.displayDate.getTime() - b.displayDate.getTime());
  }, [cancelledData, orgCancelledData, data, orgData, includeOrg, locationId, member?.id]);

  if (showActive && isLoading) {
    return (
      <div
        data-testid="simple-bookings-list-loading"
        className="bg-gray-100 border-2 border-gray-100 rounded-lg p-6 border-solid flex items-center justify-center min-h-64"
      >
        <Spinner className="size-6 mr-2" />
        <span className="text-lg">Loading bookings...</span>
      </div>
    );
  }

  if (showActive && isError) {
    return (
      <Empty
        data-testid="simple-bookings-list-error"
        className="bg-red-50 border-2 border-red-200 rounded-lg p-6 border-solid"
      >
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <AlertCircle className="text-red-500" />
          </EmptyMedia>
          <EmptyTitle className="tpr-font text-2xl text-red-700">Error loading bookings</EmptyTitle>
        </EmptyHeader>
        <EmptyDescription className="max-w-2/3 mx-auto text-center text-red-600">
          {error instanceof Error ? error.message : "Failed to load bookings. Please try again later."}
        </EmptyDescription>
      </Empty>
    );
  }

  const bookingTypeLabel = (booking: Booking): string =>
    booking.type ? membershipTypeForDisplay(booking.type as MembershipType).long : "";

  const companyBadge = (booking: Booking) =>
    isOwner && booking.memberId !== member?.id ? (
      <Badge variant="secondary" className="ml-2 text-[10px]" data-testid={`sbl-company-${booking.id}`}>
        Company{booking.member?.lastName ? ` · ${booking.member.lastName}` : ""}
      </Badge>
    ) : null;

  const ModeToggle = () => (
    <div
      className="flex justify-end items-center gap-2 mt-2 text-sm text-gray-600"
      data-testid="simple-bookings-list-mode-toggle"
    >
      <label htmlFor="simple-bookings-list-mode" className="text-xs">
        Show:
      </label>
      <select
        id="simple-bookings-list-mode"
        data-testid="simple-bookings-list-mode-select"
        value={mode}
        onChange={(e) => setShowMode(e.target.value as BookingsListMode)}
        className="bg-white border border-gray-300 rounded-md text-xs px-2 py-1"
      >
        <option value="active">Active</option>
        <option value="active-expanded">Active (all occurrences)</option>
        <option value="cancelled">Cancelled</option>
      </select>
    </div>
  );

  if (
    showActive &&
    (!allBookings || allBookings.length === 0) &&
    expiredToday.length === 0
  ) {
    return (
      <div className="flex flex-col gap-4">
        <ModeToggle />
        <Empty
          data-testid="simple-bookings-list-empty"
          className="bg-gray-100 dark:bg-slate-900 dark:border-slate-900 border-2 border-gray-100 rounded-lg p-6 border-solid"
        >
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Frown />
            </EmptyMedia>
            <EmptyTitle className="tpr-font text-2xl">No upcoming bookings</EmptyTitle>
          </EmptyHeader>
          <EmptyDescription className="max-w-2/3 mx-auto text-center">
            You don't have any upcoming bookings{locationId ? " at this location" : ""}. You can make one from any
            location you have access to.
          </EmptyDescription>
        </Empty>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <ModeToggle />
      {showActive && allBookings.length > 0 && (
    <div className="bg-gray-100 border-2 border-gray-100 rounded-lg p-2 border-solid">
      <Table data-testid="simple-bookings-list-table">
        <TableHeader>
          <TableRow>
            <TableHead>Next date</TableHead>
            <TableHead>Time</TableHead>
            {!locationId && <TableHead>Location</TableHead>}
            <TableHead>Room</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Ref</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {allBookings.map((booking) => (
            <Fragment key={booking.id}>
              <TableRow
                className="cursor-pointer hover:bg-gray-200"
                data-testid={`simple-bookings-list-row-${booking.id}`}
                onClick={() => navigate(`/view-booking/${booking.id}?returnUrl=${encodeURIComponent(returnUrl)}`)}
              >
                <TableCell>
                  <div className="flex items-start gap-1">
                    {mode === "active-expanded" && getDisplayRecurrence(booking) && (
                      <button
                        type="button"
                        data-testid={`sbl-expand-${booking.id}`}
                        aria-expanded={expandedRows.has(booking.id)}
                        aria-label="Show all occurrences"
                        className="mt-0.5 text-gray-500 hover:text-gray-800"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleRow(booking.id);
                        }}
                      >
                        {expandedRows.has(booking.id) ? (
                          <ChevronDown className="size-4" />
                        ) : (
                          <ChevronRight className="size-4" />
                        )}
                      </button>
                    )}
                    <div>
                      <span className="block">
                        {booking.displayDateLabel || humaniseDateFriendly(booking.displayDate)}
                      </span>
                      {(() => {
                        const displayRecurrence = getDisplayRecurrence(booking);
                        if (!displayRecurrence) return null;
                        return (
                          <span
                            className="block text-xs text-gray-500 mt-0.5"
                            data-testid={`simple-bookings-list-recurrence-caption-${booking.id}`}
                          >
                            <RecurrenceRenderer recurrence={displayRecurrence} />{" "}
                            at {booking.startTime.slice(0, 2)}:{booking.startTime.slice(2)}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <span className="truncate overflow-hidden whitespace-nowrap">
                    {booking.startTime.slice(0, 2)}:{booking.startTime.slice(2)}{" "}
                    <span className="hidden md:inline text-gray-600">
                      ({humanizeDuration(booking.startTime, booking.endTime)})
                    </span>
                  </span>
                </TableCell>
                {!locationId && (
                  <TableCell>
                    <span className="block md:hidden">
                      <LocationShorthandPill location={booking.location?.name ?? ""} />
                    </span>
                    <span className="hidden md:block">{booking.location?.name}</span>
                  </TableCell>
                )}
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span className={`w-3 h-3 rounded-full ${booking.room?.swatch}`} />
                    <span className="hidden md:block">{booking.room?.name}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <span
                    className="inline-block px-2 py-0.5 rounded-full bg-gray-200 text-xs font-semibold text-gray-700"
                    data-testid={`simple-bookings-list-type-${booking.id}`}
                  >
                    {bookingTypeLabel(booking)}
                  </span>
                  {companyBadge(booking)}
                </TableCell>
                <TableCell>
                  <span
                    className="font-mono text-xs uppercase text-gray-700"
                    data-testid={`simple-bookings-list-ref-${booking.id}`}
                  >
                    {booking.shortCode || "—"}
                  </span>
                </TableCell>
              </TableRow>
              {mode === "active-expanded" && expandedRows.has(booking.id) && (
                <TableRow
                  data-testid={`sbl-occurrences-${booking.id}`}
                  className="hover:bg-transparent"
                >
                  {/* Same grey background as the list (no white block) so occurrences read as
                      hanging off their genesis row. */}
                  <TableCell colSpan={totalCols} className="bg-gray-100 pt-0">
                    <OccurrenceTree booking={booking} />
                  </TableCell>
                </TableRow>
              )}
            </Fragment>
          ))}
        </TableBody>
      </Table>
    </div>
      )}

      {showActive && expiredToday.length > 0 && (
        <div
          className="bg-gray-50 border border-gray-200 rounded-lg p-2"
          data-testid="simple-bookings-list-expired-section"
        >
          <p className="text-sm text-gray-600 px-2 pt-1 pb-2">
            Bookings today that have already finished
          </p>
          <Table data-testid="simple-bookings-list-expired-table">
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Time</TableHead>
                {!locationId && <TableHead>Location</TableHead>}
                <TableHead>Room</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Ref</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {expiredToday.map((booking) => (
                <TableRow
                  key={`expired-${booking.id}`}
                  className="cursor-pointer hover:bg-gray-200 text-gray-500"
                  data-testid={`simple-bookings-list-expired-row-${booking.id}`}
                  onClick={() => navigate(`/view-booking/${booking.id}?returnUrl=${encodeURIComponent(returnUrl)}`)}
                >
                  <TableCell>Today</TableCell>
                  <TableCell>
                    <span className="truncate overflow-hidden whitespace-nowrap">
                      {booking.startTime.slice(0, 2)}:{booking.startTime.slice(2)}{" "}
                      <span className="hidden md:inline">
                        ({humanizeDuration(booking.startTime, booking.endTime)})
                      </span>
                    </span>
                  </TableCell>
                  {!locationId && (
                    <TableCell>
                      <span className="block md:hidden">
                        <LocationShorthandPill location={booking.location?.name ?? ""} />
                      </span>
                      <span className="hidden md:block">{booking.location?.name}</span>
                    </TableCell>
                  )}
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className={`w-3 h-3 rounded-full ${booking.room?.swatch}`} />
                      <span className="hidden md:block">{booking.room?.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="inline-block px-2 py-0.5 rounded-full bg-gray-200 text-xs font-semibold text-gray-600">
                      {bookingTypeLabel(booking)}
                    </span>
                    {companyBadge(booking)}
                  </TableCell>
                  <TableCell>
                    <span className="font-mono text-xs uppercase">{booking.shortCode || "—"}</span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {mode === "cancelled" && (
        <div
          className="bg-gray-100 border-2 border-gray-100 rounded-lg p-2 border-solid"
          data-testid="simple-bookings-list-cancelled-section"
        >
          <p className="text-sm text-gray-600 px-2 pt-1 pb-2">
            Cancelled bookings — last month and the next year
          </p>
          {isLoadingCancelled ? (
            <div
              className="flex items-center justify-center min-h-32"
              data-testid="simple-bookings-list-cancelled-loading"
            >
              <Spinner className="size-6 mr-2" />
              <span className="text-lg">Loading cancelled bookings...</span>
            </div>
          ) : cancelledBookings.length === 0 ? (
            <Empty
              data-testid="simple-bookings-list-cancelled-empty"
              className="bg-gray-50 border-2 border-gray-100 rounded-lg p-6 border-solid"
            >
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <Frown />
                </EmptyMedia>
                <EmptyTitle className="tpr-font text-2xl">No cancelled bookings</EmptyTitle>
              </EmptyHeader>
              <EmptyDescription className="max-w-2/3 mx-auto text-center">
                You don't have any cancelled bookings{locationId ? " at this location" : ""} in the last month or
                upcoming year.
              </EmptyDescription>
            </Empty>
          ) : (
            <Table data-testid="simple-bookings-list-cancelled-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Time</TableHead>
                  {!locationId && <TableHead>Location</TableHead>}
                  <TableHead>Room</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Ref</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cancelledBookings.map((booking) => (
                  <TableRow
                    key={`cancelled-${booking.id}`}
                    className="cursor-pointer hover:bg-gray-200 text-gray-500"
                    data-testid={`simple-bookings-list-cancelled-row-${booking.id}`}
                    onClick={() => navigate(`/view-booking/${booking.id}?returnUrl=${encodeURIComponent(returnUrl)}`)}
                  >
                    <TableCell>
                      <span className="block">
                        {booking.displayDateLabel || humaniseDateFriendly(booking.displayDate)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="line-through truncate overflow-hidden whitespace-nowrap">
                        {booking.startTime.slice(0, 2)}:{booking.startTime.slice(2)}{" "}
                        <span className="hidden md:inline">
                          ({humanizeDuration(booking.startTime, booking.endTime)})
                        </span>
                      </span>
                    </TableCell>
                    {!locationId && (
                      <TableCell>
                        <span className="block md:hidden line-through">
                          <LocationShorthandPill location={booking.location?.name ?? ""} />
                        </span>
                        <span className="hidden md:block line-through">{booking.location?.name}</span>
                      </TableCell>
                    )}
                    <TableCell>
                      <div className="flex items-center gap-2 line-through">
                        <span className={`w-3 h-3 rounded-full ${booking.room?.swatch}`} />
                        <span className="hidden md:block">{booking.room?.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="inline-block px-2 py-0.5 rounded-full bg-gray-200 text-xs font-semibold text-gray-600">
                        {bookingTypeLabel(booking)}
                      </span>
                      {companyBadge(booking)}
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-xs uppercase">{booking.shortCode || "—"}</span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      )}
    </div>
  );
};

export default SimpleBookingsList;
